import router, { addRoutes } from "@/router";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import getPageTitle from "@/utils/get-page-title";
import { login } from "@/services/keycloak";

NProgress.configure({ showSpinner: true });

// Paths that don't require authentication at all
const noAuthList = [
  /^\/link\/noauth/,
  /^\/register/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/activate/,
];

// Paths that require authentication but not account context
export const noAccountList = [...noAuthList, /^\/accounts/, /^\/login/];

export function pathNoAuth(path: string): boolean {
  return !!noAuthList.find((url) => path.match(url));
}

function pathNoAccount(path: string): boolean {
  return !!noAccountList.find((url) => path.match(url));
}

function isOAuthCallback(hash: string): boolean {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  return params.has("state") && (params.has("code") || params.has("error"));
}

let routesAdded = false;

router.beforeEach(async (to) => {
  // Start progress bar
  NProgress.start();

  // Set page title
  document.title = getPageTitle(to.meta?.title as string);

  // Keycloak answers the login in the URL fragment (#state=…&code=…). keycloak-js removes it from
  // the address bar, but the router read the location before that — drop it here, so it never
  // ends up in a page or a `redirect` target.
  if (isOAuthCallback(to.hash)) {
    return { path: to.path, query: to.query, hash: "", replace: true };
  }

  // Allow access to pages that don't require authentication
  if (pathNoAuth(to.path)) {
    return true;
  }

  // Lazy import stores to avoid circular dependencies
  const { useAuthStore } = await import("@/stores/auth");
  const authStore = useAuthStore();

  // Without a session: Keycloak's login page, and back to this page afterwards. Normally the
  // app is only mounted with a session (main.ts); this covers a session that ended meanwhile.
  if (!authStore.isAuthenticated) {
    await login({ path: to.fullPath });
    return false;
  }

  // /login and /login/<domain> (old bookmarks and links): signed in by now, so into the app —
  // to the `redirect` target the old links carry, if any.
  if (to.path.startsWith("/login")) {
    const redirect = (to.query.redirect ?? to.query.target) as string | undefined;
    return redirect && redirect.startsWith("/") && !redirect.startsWith("//")
      ? redirect
      : "/";
  }

  // Allow access to pages that require auth but not account context (e.g., /accounts)
  if (pathNoAccount(to.path)) {
    return true;
  }

  // Lazy import remaining stores only when needed
  const { useAccountStore } = await import("@/stores/account");
  const { usePermissionStore } = await import("@/stores/permission");

  const accountStore = useAccountStore();
  const permissionStore = usePermissionStore();

  // Check if routes have already been added
  if (routesAdded) {
    return true;
  }

  try {
    // Get current account
    const account = await accountStore.getCurrentAccount();

    // Generate routes based on permissions
    const accessRoutes = permissionStore.generateRoutes(account.data);

    // Add routes to router
    addRoutes(accessRoutes);
    routesAdded = true;

    // Redirect to the same route to ensure the new routes are available
    return { ...to, replace: true };
  } catch {
    // If account fetch fails, redirect to accounts page — keeping the target,
    // which the account picker honours once an account is chosen.
    const path = to.fullPath.split("?")[0];
    if (path === "/" || pathNoAccount(path)) {
      return "/accounts";
    }
    return { path: "/accounts", query: { redirect: to.fullPath } };
  }
});

router.afterEach(() => {
  NProgress.done();
});

// Export function to reset routes added flag (used on logout)
export function resetRoutesAdded() {
  routesAdded = false;
}
